import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../services/api';
import { useAuth } from './useAuth';

export type ThemePref = 'system' | 'light' | 'dark';
export type NavPosition = 'top' | 'bottom';

interface Preferences {
  theme: ThemePref;
  navPosition: NavPosition;
}

interface PreferencesContextValue extends Preferences {
  setTheme: (theme: ThemePref) => void;
  setNavPosition: (pos: NavPosition) => void;
  resolvedTheme: 'light' | 'dark';
}

const STORAGE_KEY = 'yp_portal_prefs';
const DEFAULTS: Preferences = { theme: 'system', navPosition: 'top' };

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function readLocal(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    const theme = parsed.theme;
    const navPosition = parsed.navPosition;
    return {
      theme: theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'system',
      navPosition: navPosition === 'bottom' ? 'bottom' : 'top',
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function writeLocal(prefs: Preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function resolveTheme(theme: ThemePref): 'light' | 'dark' {
  if (theme === 'light' || theme === 'dark') return theme;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Preferences>(() => readLocal());
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    resolveTheme(readLocal().theme),
  );
  const prefsEpoch = useRef(0);

  useLayoutEffect(() => {
    const next = resolveTheme(prefs.theme);
    setResolvedTheme(next);
    document.documentElement.dataset.theme = next;
    document.documentElement.dataset.navPosition = prefs.navPosition;
  }, [prefs.theme, prefs.navPosition]);

  useEffect(() => {
    if (prefs.theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => {
      const next = resolveTheme('system');
      setResolvedTheme(next);
      document.documentElement.dataset.theme = next;
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [prefs.theme]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const epochAtStart = prefsEpoch.current;
    (async () => {
      const res = await api.getUiSettings();
      if (cancelled || !res.data) return;
      // Ignore stale server prefs if user changed theme while request was in flight.
      if (prefsEpoch.current !== epochAtStart) return;
      const next: Preferences = {
        theme:
          res.data.theme === 'light' || res.data.theme === 'dark' || res.data.theme === 'system'
            ? res.data.theme
            : 'system',
        navPosition: res.data.nav_position === 'bottom' ? 'bottom' : 'top',
      };
      const local = readLocal();
      const localExplicit = local.theme === 'light' || local.theme === 'dark';
      if (localExplicit) {
        if (local.theme !== next.theme || local.navPosition !== next.navPosition) {
          void api.updateUiSettings({
            theme: local.theme,
            nav_position: local.navPosition,
          });
        }
        return;
      }
      // If local already has an explicit non-default choice and server still has defaults,
      // keep local (avoids flicker when sync hasn't caught up / first save pending).
      if (
        (local.theme !== 'system' || local.navPosition !== 'top') &&
        next.theme === 'system' &&
        next.navPosition === 'top' &&
        (local.theme !== next.theme || local.navPosition !== next.navPosition)
      ) {
        void api.updateUiSettings({
          theme: local.theme,
          nav_position: local.navPosition,
        });
        return;
      }
      setPrefs(next);
      writeLocal(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const persist = useCallback(
    async (next: Preferences) => {
      prefsEpoch.current += 1;
      setPrefs(next);
      writeLocal(next);
      if (!user) return;
      await api.updateUiSettings({
        theme: next.theme,
        nav_position: next.navPosition,
      });
    },
    [user],
  );

  const setTheme = useCallback(
    (theme: ThemePref) => {
      void persist({ ...prefs, theme });
    },
    [persist, prefs],
  );

  const setNavPosition = useCallback(
    (navPosition: NavPosition) => {
      void persist({ ...prefs, navPosition });
    },
    [persist, prefs],
  );

  const value = useMemo(
    () => ({
      theme: prefs.theme,
      navPosition: prefs.navPosition,
      setTheme,
      setNavPosition,
      resolvedTheme,
    }),
    [prefs.theme, prefs.navPosition, setTheme, setNavPosition, resolvedTheme],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
